import { ILocationSearchResult } from '@travelnest/merlin.location-search';
import retry from 'async-retry';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { api as dataApi } from '../../../data/api';
import { IProperty } from '../../../data/entities/property';
import { IState } from '../../../store/reducer';
import { locationFromGoogleAddressComponents } from './helpers';
import { PropertyShellAddressStep } from './steps/PropertyShellAddress';
import { PropertyShellCompletedStep } from './steps/PropertyShellCompleted';
import { PropertyShellIntroductionStep } from './steps/PropertyShellIntroduction';
import { PropertyShellPromotionStep } from './steps/PropertyShellPromotions';
import { PropertyShellTypeStep } from './steps/PropertyShellType';

interface IPropertyShellModalProps {
	onRequestClose: () => any;
	showModal?: boolean;
}

enum PropertyShellModalStep {
	INTRO = 1,
	TYPE = 2,
	ADDRESS = 3,
	COMPLETED = 4,
	PROMOTIONS = 5,
}

const onRequestSave = async (
	type: string,
	bedrooms: number,
	extraBeds: number,
	address: ILocationSearchResult,
	ownerId: string,
) => {
	// Get Property Shell
	const propertyShell = await dataApi.propertyShell.create({
		property_type: type,
		bedroom_number: bedrooms, // Note: This appears to not actually be part of the type?
		extra_bed_number: extraBeds,
		...locationFromGoogleAddressComponents(address),
	} as unknown as Partial<IProperty>); // Workaround for an interface issue until it's fixed

	// Create Property
	const property = await dataApi.properties.create({
		...propertyShell,
		owner_id: ownerId,
	});

	// Get Description
	const generatedDescription = await retry(
		async () =>
			await api.propertyDescription.getPropertyDescription(property.id),
		{
			retries: 5,
			minTimeout: 1000,
			factor: 1,
		}
	);

	// Update Property
	await dataApi.properties.update({
		...property,
		id: property.id,
		long_description: generatedDescription.description,
	} as any);

	return property.id;
};

export const PropertyShellModal: React.FC<IPropertyShellModalProps> = (props) => {
	const [modalStep, setModalStep] = useState(PropertyShellModalStep.INTRO);
	const [propertyType, setPropertyType] = useState('house');
	const [bedrooms, setBedrooms] = useState(1);
	const [address, setAddress] = useState<ILocationSearchResult | null>(null);
	const [propertyId, setPropertyId] = useState<number | undefined>(undefined);
	const navigate = useNavigate();
	const ownerId = useSelector<IState, string>((state) => state.entities.auth.session.current.ownerId);

	switch (modalStep) {
		case PropertyShellModalStep.COMPLETED:
			return <PropertyShellCompletedStep
				onRequestNext={() => setModalStep(PropertyShellModalStep.PROMOTIONS)}
				onRequestPrevious={() => undefined}
				onRequestClose={() => {
					props.onRequestClose();
					window.location.reload();
				}}
				onRequestSave={async () => {
					const propId = await onRequestSave(propertyType, bedrooms, bedrooms === 0 ? 1 : 0, address!, ownerId);
					setPropertyId(propId);
					return propId;
				}}
			/>;
		case PropertyShellModalStep.PROMOTIONS:
			return <PropertyShellPromotionStep
				onRequestNext={() => {
					props.onRequestClose();
					navigate(`/property/${propertyId}/add`);
				}}
				onRequestPrevious={() => setModalStep(PropertyShellModalStep.ADDRESS)}
				onRequestClose={props.onRequestClose}
				propertyId={propertyId!}
				ownerId={ownerId}
			/>;
		case PropertyShellModalStep.ADDRESS:
			return <PropertyShellAddressStep
				onRequestNext={() => setModalStep(PropertyShellModalStep.COMPLETED)}
				onRequestPrevious={() => setModalStep(PropertyShellModalStep.TYPE)}
				onRequestClose={() => props.onRequestClose()}
				address={address}
				onAddressUpdate={(newAddress) => setAddress(newAddress)}
			/>;
		case PropertyShellModalStep.TYPE:
			return <PropertyShellTypeStep
				onRequestNext={() => setModalStep(PropertyShellModalStep.ADDRESS)}
				onRequestPrevious={() => setModalStep(PropertyShellModalStep.INTRO)}
				onRequestClose={props.onRequestClose}
				bedrooms={bedrooms}
				onBedroomsUpdate={(rooms) => setBedrooms(rooms)}
				propertyType={propertyType}
				onPropertyTypeUpdate={(type) => setPropertyType(type)}
			/>;
		default:
		case PropertyShellModalStep.INTRO:
			return <PropertyShellIntroductionStep
				onRequestNext={() => setModalStep(PropertyShellModalStep.TYPE)}
				onRequestPrevious={() => undefined}
				onRequestClose={props.onRequestClose}
			/>;
	}
};
